import { registerPlugin } from '@capacitor/core';

const Reminder = registerPlugin('Reminder');

// ─ Reminder IDs ──────────────────────────────────────────────────────────────
export const REMINDER_IDS = {
  FINANCIAL: 1001,
  HYDRATION: 1002,
  STRETCH:   1003,
};

// Wellness reminder IDs (2001-2004)
export const WELLNESS_IDS = {
  EYE_BREAK:     2001,
  DRINK_WATER:   2002,
  STAND_STRETCH: 2003,
  WALK:          2004,
};

// ─ Defaults ──────────────────────────────────────────────────────────────────
export const REMINDER_DEFAULTS = {
  FINANCIAL: {
    title:   'Spera',
    message: "Don't forget to log your expenses!",
    frequency: 'daily',
    timeHour: 21,
    timeMinute: 0,
  },
  HYDRATION: {
    title:   'Hydration',
    message: 'Time to hydrate! Drink a glass of water.',
    frequency: 'interval',
    intervalMinutes: 120,
  },
  STRETCH: {
    title:   'Break Time',
    message: 'Take a break. Stand up and stretch.',
    frequency: 'interval',
    intervalMinutes: 90,
  },
};

export const WELLNESS_DEFAULTS = {
  EYE_BREAK:     { title: 'Eye Break',      message: 'Look 20 feet away for 20 seconds. Rest your eyes.', frequency: 'interval', intervalMinutes: 20  },
  DRINK_WATER:   { title: 'Hydrate',        message: 'Drink a glass of water now.',                       frequency: 'interval', intervalMinutes: 30  },
  STAND_STRETCH: { title: 'Stand & Stretch',message: 'Stand up and stretch for a moment.',                frequency: 'interval', intervalMinutes: 50  },
  WALK:          { title: 'Walk Break',     message: 'Take a 2–5 minute walk. Your body needs it.',       frequency: 'interval', intervalMinutes: 120 },
};

// ─ Permissions ───────────────────────────────────────────────────────────────
export async function requestNotificationPermission() {
  return Reminder.requestPermissions();
}

export async function checkNotificationPermission() {
  return Reminder.checkPermissions();
}

async function ensurePermission() {
  const { notifications } = await checkNotificationPermission();
  if (notifications === 'granted') return true;
  const result = await requestNotificationPermission();
  return result.notifications === 'granted';
}

// ─ Trigger computation ───────────────────────────────────────────────────────

/**
 * Compute next trigger timestamp (ms) for a reminder config.
 * Handles daily, interval (days or minutes), and custom-weekday modes.
 */
function computeNextTrigger(settings) {
  const now = Date.now();

  if (settings.frequency === 'interval') {
    // Sub-day interval (hydration/stretch) — intervalMinutes takes priority
    if (settings.intervalMinutes) {
      return now + settings.intervalMinutes * 60 * 1000;
    }
    // Day-level interval (legacy financial reminder support)
    const days = Math.max(Number(settings.intervalDays) || 1, 1);
    return now + days * 86400000;
  }

  const hour   = settings.timeHour   ?? 21;
  const minute = settings.timeMinute ?? 0;

  if (settings.frequency === 'custom' && settings.customDays?.length > 0) {
    const DAY_MAP = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };
    const indices = settings.customDays.map(d => DAY_MAP[d]).filter(d => d !== undefined);
    for (let offset = 0; offset <= 7; offset++) {
      const candidate = new Date();
      candidate.setDate(candidate.getDate() + offset);
      candidate.setHours(hour, minute, 0, 0);
      if (indices.includes(candidate.getDay()) && candidate.getTime() > now) {
        return candidate.getTime();
      }
    }
  }

  // daily (default)
  const target = new Date();
  target.setHours(hour, minute, 0, 0);
  if (target.getTime() <= now) target.setDate(target.getDate() + 1);
  return target.getTime();
}

// ─ Schedule / Cancel ─────────────────────────────────────────────────────────

/**
 * Schedule one of the three reminder types.
 * @param {'FINANCIAL'|'HYDRATION'|'STRETCH'} type
 * @param {object} settings - Merged user settings (overrides REMINDER_DEFAULTS[type])
 */
export async function scheduleTypedReminder(type, settings = {}) {
  if (!(await ensurePermission())) return { scheduled: false, reason: 'permission_denied' };

  const id      = REMINDER_IDS[type];
  const def     = REMINDER_DEFAULTS[type];
  const merged  = { ...def, ...settings };
  const triggerAt = computeNextTrigger(merged);

  await Reminder.schedule({
    id,
    triggerAt,
    title:          merged.title,
    message:        merged.message,
    frequency:      merged.frequency,
    intervalDays:   merged.intervalDays  ?? 1,
    intervalMinutes: merged.intervalMinutes ?? 0,
    customDays:     (merged.customDays || []).join(','),
    timeHour:       merged.timeHour   ?? 21,
    timeMinute:     merged.timeMinute ?? 0,
  });

  return { scheduled: true };
}

/**
 * Cancel a reminder by type.
 * @param {'FINANCIAL'|'HYDRATION'|'STRETCH'} type
 */
export async function cancelTypedReminder(type) {
  return Reminder.cancel({ id: REMINDER_IDS[type] });
}

// ─ Wellness reminders ────────────────────────────────────────────────────────

/**
 * Schedule a single wellness reminder by type.
 * @param {'EYE_BREAK'|'DRINK_WATER'|'STAND_STRETCH'|'WALK'} type
 * @param {boolean} enabled
 */
export async function scheduleWellnessReminder(type, enabled) {
  const id = WELLNESS_IDS[type];
  if (!id) return;
  if (!enabled) {
    try { await Reminder.cancel({ id }); } catch { /* ok */ }
    return;
  }
  if (!(await ensurePermission())) return;
  const def = WELLNESS_DEFAULTS[type];
  const triggerAt = computeNextTrigger(def);
  await Reminder.schedule({
    id,
    triggerAt,
    title:           def.title,
    message:         def.message,
    frequency:       'interval',
    intervalDays:    0,
    intervalMinutes: def.intervalMinutes,
    customDays:      '',
    timeHour:        0,
    timeMinute:      0,
  });
}

/**
 * Cancel all wellness reminders (used when disabling wellness mode entirely).
 */
export async function cancelAllWellnessReminders() {
  for (const id of Object.values(WELLNESS_IDS)) {
    try { await Reminder.cancel({ id }); } catch { /* ok */ }
  }
}

// ─ Backward-compatible alias (used by existing Profile.js code) ───────────────
/** @deprecated Use scheduleTypedReminder('FINANCIAL', settings) instead */
export async function scheduleConfiguredReminder(settings) {
  const timeDate = settings.time instanceof Date ? settings.time : new Date();
  return scheduleTypedReminder('FINANCIAL', {
    frequency:    settings.frequency,
    intervalDays: settings.intervalDays,
    customDays:   settings.customDays,
    timeHour:     timeDate.getHours(),
    timeMinute:   timeDate.getMinutes(),
  });
}

/** @deprecated Use cancelTypedReminder('FINANCIAL') instead */
export async function cancelReminder(id) {
  return Reminder.cancel({ id });
}

export const DAILY_REMINDER_ID = REMINDER_IDS.FINANCIAL;
